import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button, Empty, Field, Input, Modal, Notice, Panel, Select, Table, type ColumnDef,
} from '../design/index.js'
import {
  useApi,
  type EmailCopyOptions,
  type EmailTemplateView,
  type OutboxMessage,
  type PrintTemplateView,
  type TemplateSlot,
} from '../app/api.js'
import { ErrorBlock, Loading, messageFor } from '../framework/feedback.js'
import { formatDateTime } from '../framework/format.js'

/**
 * Print and email templates (docs/11 §Data, §Email Templates).
 *
 * The editor is deliberately plain text rather than a WYSIWYG canvas: what is
 * stored IS the template, a rich editor would silently rewrite the expressions
 * inside it, and the people who write these are the same people who wrote the
 * ones on the system being replaced.
 *
 * Preview renders against a real document of that type rather than a fixture,
 * because the mistakes that matter — a missing tax column, a total that reads
 * as `[object Object]` — only appear against real data.
 */

/* ========================= print templates ================================ */

interface Draft {
  id?: string
  docType: string
  name: string
  fileName: string
  body: string
  isDefault: boolean
  appliesToAll: boolean
}

const draftFor = (slot: TemplateSlot, template: PrintTemplateView | undefined, standard: string): Draft => ({
  ...(template ? { id: template.id } : {}),
  docType: slot.docType,
  name: template?.name ?? `${slot.docTypeLabel} — company layout`,
  fileName: template?.fileName ?? '#{data.doc_no}',
  body: template?.body ?? standard,
  isDefault: template?.isDefault ?? true,
  appliesToAll: template?.appliesToAll ?? false,
})

export const PrintTemplates = () => {
  const api = useApi()
  const client = useQueryClient()

  const slots = useQuery({ queryKey: ['print-templates', api.companyId], queryFn: () => api.printTemplates() })
  const standard = useQuery({ queryKey: ['print-standard'], queryFn: () => api.standardPrintTemplate() })
  const helpers = useQuery({ queryKey: ['print-helpers'], queryFn: () => api.printTemplateHelpers() })

  const [selected, setSelected] = useState<string>('')
  const [draft, setDraft] = useState<Draft | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showReference, setShowReference] = useState(false)

  const slot = useMemo(
    () => slots.data?.find((s) => s.docType === selected),
    [slots.data, selected],
  )

  const fields = useQuery({
    queryKey: ['print-fields', api.companyId, selected],
    queryFn: () => api.printTemplateFields(selected),
    enabled: showReference && selected !== '',
  })

  // Opening a type loads its default template, or the standard body if it has
  // none — so "edit" and "start from the shipped layout" are one action.
  useEffect(() => {
    if (!slot || !standard.data) return
    setDraft(draftFor(slot, slot.templates.find((t) => t.isDefault) ?? slot.templates[0], standard.data.body))
    setError(null)
  }, [slot, standard.data])

  const invalidate = () => {
    void client.invalidateQueries({ queryKey: ['print-templates', api.companyId] })
  }

  const save = useMutation({
    mutationFn: async () => {
      if (!draft) throw new Error('Nothing to save.')
      return api.savePrintTemplate({
        ...(draft.id ? { id: draft.id } : {}),
        docType: draft.docType,
        name: draft.name,
        fileName: draft.fileName.trim() === '' ? null : draft.fileName,
        body: draft.body,
        isDefault: draft.isDefault,
        appliesToAll: draft.appliesToAll,
      })
    },
    onSuccess: () => { setError(null); invalidate() },
    onError: (err) => setError(messageFor(err)),
  })

  const reset = useMutation({
    mutationFn: () => api.resetPrintTemplate(selected),
    onSuccess: () => { setError(null); invalidate() },
    onError: (err) => setError(messageFor(err)),
  })

  const render = useMutation({
    mutationFn: async () => {
      if (!draft) throw new Error('Nothing to preview.')
      return api.previewPrintTemplate(draft.docType, draft.body)
    },
    onSuccess: (html) => { setError(null); setPreview(html) },
    onError: (err) => setError(messageFor(err)),
  })

  if (slots.isLoading || standard.isLoading) return <Loading />
  if (slots.error) return <ErrorBlock error={slots.error} />

  const groups = new Map<string, TemplateSlot[]>()
  for (const s of slots.data ?? []) {
    const bucket = groups.get(s.group)
    if (bucket) bucket.push(s)
    else groups.set(s.group, [s])
  }

  return (
    <>
      <Panel title="Document types">
        <p className="helper">
          A type with no template of its own prints with the standard layout. Choose one to
          write a company layout for it.
        </p>
        <div className="tile-grid">
          {[...groups].map(([group, list]) => (
            <div className="tile" key={group}>
              <div className="tile__body">
                <div className="tile__title">{group}</div>
                <ul className="plain-list">
                  {list.map((s) => (
                    <li key={s.docType}>
                      <button
                        type="button"
                        className={s.docType === selected ? 'linkish linkish--on' : 'linkish'}
                        onClick={() => setSelected(s.docType)}
                      >
                        {s.docTypeLabel}
                      </button>
                      {/* docs/15 R-12.2 — a shipped layout says so, so nobody
                          goes looking for the template that changed it. */}
                      <span className="pill">
                        {s.templates.length > 0 ? 'own layout' : 'standard'}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </Panel>

      {draft && slot ? (
        <Panel
          title={`${slot.docTypeLabel} layout`}
          aside={
            <div className="row">
              <Button kind="ghost" onClick={() => setShowReference((on) => !on)}>
                {showReference ? 'Hide reference' : 'What can I write?'}
              </Button>
              <Button kind="ghost" disabled={render.isPending} onClick={() => render.mutate()}>
                Preview
              </Button>
              <Button
                kind="ghost"
                disabled={reset.isPending || slot.templates.length === 0}
                onClick={() => reset.mutate()}
              >
                Back to standard
              </Button>
              <Button kind="primary" disabled={save.isPending} onClick={() => save.mutate()}>
                Save
              </Button>
            </div>
          }
        >
          {error ? <Notice kind="bad">{error}</Notice> : null}

          <div className="form-grid">
            <Field label="Template name">
              <Input value={draft.name} onChange={(name) => setDraft({ ...draft, name })} />
            </Field>
            <Field
              label="File name"
              hint="An expression. #{data.doc_no} and {{doc_no}} both work."
            >
              <Input value={draft.fileName} onChange={(fileName) => setDraft({ ...draft, fileName })} />
            </Field>
          </div>

          <div className="row row--wrap">
            <label className="column-choice">
              <input
                type="checkbox"
                checked={draft.isDefault}
                onChange={() => setDraft({ ...draft, isDefault: !draft.isDefault })}
              />
              <span>Print this type with this layout</span>
            </label>
            <label className="column-choice">
              <input
                type="checkbox"
                checked={draft.appliesToAll}
                onChange={() => setDraft({ ...draft, appliesToAll: !draft.appliesToAll })}
              />
              <span>Use it in every company on this account</span>
            </label>
          </div>

          <Field label="Template">
            <textarea
              className="control control--code"
              style={{ minHeight: 420 }}
              spellCheck={false}
              value={draft.body}
              onChange={(event) => setDraft({ ...draft, body: event.currentTarget.value })}
            />
          </Field>

          {showReference ? (
            <div className="two-col">
              <div>
                <h3 className="subhead">Helpers</h3>
                <table className="tbl tbl--compact">
                  <tbody>
                    {(helpers.data ?? []).map((h) => (
                      <tr key={h.name}>
                        <td className="mono">{h.usage}</td>
                        <td>{h.means}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div>
                <h3 className="subhead">Values on this document</h3>
                {fields.isLoading ? <Loading what="Reading a document…" /> : null}
                <ul className="plain-list mono">
                  {(fields.data ?? []).map((name) => (
                    <li key={name}>{`{{${name}}}`}</li>
                  ))}
                </ul>
              </div>
            </div>
          ) : null}
        </Panel>
      ) : (
        <Empty
          title="Pick a document type"
          body={['Choose a type above to see the layout it prints with and to write your own.']}
        />
      )}

      {preview !== null ? (
        <Modal
          title="Preview"
          subtitle="Rendered against the most recent document of this type."
          wide
          actions={<Button kind="ghost" onClick={() => setPreview(null)}>Close</Button>}
          onClose={() => setPreview(null)}
        >
          {/* Sandboxed: a template is administrator input, and it renders here
              with no access to the session it was previewed from. */}
          <iframe
            title="Template preview"
            sandbox=""
            srcDoc={preview}
            style={{ width: '100%', height: '60vh', border: '1px solid var(--line)', borderRadius: 'var(--radius-md)', background: '#fff' }}
          />
        </Modal>
      ) : null}
    </>
  )
}

/* ========================= email templates ================================ */

export const EmailTemplates = () => {
  const api = useApi()
  const client = useQueryClient()
  const templates = useQuery({ queryKey: ['mail-templates', api.companyId], queryFn: () => api.mailTemplates() })
  const status = useQuery({ queryKey: ['mail-status'], queryFn: () => api.mailStatus() })

  const [editing, setEditing] = useState<
    { key: string; subject: string; body: string; copies: EmailCopyOptions } | null
  >(null)
  const [error, setError] = useState<string | null>(null)

  const invalidate = () => {
    void client.invalidateQueries({ queryKey: ['mail-templates', api.companyId] })
  }

  const save = useMutation({
    mutationFn: async () => {
      if (!editing) throw new Error('Nothing to save.')
      return api.saveMailTemplate(editing.key, editing.subject, editing.body, editing.copies)
    },
    onSuccess: () => { setEditing(null); setError(null); invalidate() },
    onError: (err) => setError(messageFor(err)),
  })

  const reset = useMutation({
    mutationFn: (key: string) => api.resetMailTemplate(key),
    onSuccess: () => { setEditing(null); setError(null); invalidate() },
    onError: (err) => setError(messageFor(err)),
  })

  if (templates.isLoading) return <Loading />
  if (templates.error) return <ErrorBlock error={templates.error} />

  const columns: ReadonlyArray<ColumnDef<EmailTemplateView>> = [
    { key: 'label', header: 'Email', width: 240 },
    { key: 'when', header: 'Sent when' },
    { key: 'subject', header: 'Subject', width: 300 },
    {
      key: 'customised',
      header: 'Wording',
      width: 140,
      render: (row) => (row.customised ? 'Yours' : 'As shipped'),
    },
  ]

  return (
    <>
      {status.data && !status.data.transportConfigured ? (
        <Notice kind="warn">
          No mail server is configured, so messages are queued and held rather than sent.
          Set SMTP_URL on the server to release them.
        </Notice>
      ) : null}

      <Panel title="Templates">
        <p className="helper">
          Do not sign these off yourself — the sender's own signature is appended to every
          message automatically (docs/15 R-12.4).
        </p>
        <Table
          columns={columns}
          rows={templates.data ?? []}
          getKey={(row) => row.key}
          onRowClick={(row) => {
            setError(null)
            setEditing({ key: row.key, subject: row.subject, body: row.body, copies: row.copies })
          }}
          empty={<Empty title="No templates" body={['Nothing to configure yet.']} />}
        />
      </Panel>

      {editing ? (
        <Modal
          title={templates.data?.find((t) => t.key === editing.key)?.label ?? 'Email'}
          subtitle={templates.data?.find((t) => t.key === editing.key)?.when}
          wide
          actions={
            <>
              <Button kind="ghost" onClick={() => reset.mutate(editing.key)} disabled={reset.isPending}>
                Back to the shipped wording
              </Button>
              <Button kind="ghost" onClick={() => setEditing(null)}>Cancel</Button>
              <Button kind="primary" onClick={() => save.mutate()} disabled={save.isPending}>Save</Button>
            </>
          }
          onClose={() => setEditing(null)}
        >
          {error ? <Notice kind="bad">{error}</Notice> : null}
          <p className="helper">
            Placeholders:{' '}
            <span className="mono">
              {(templates.data?.find((t) => t.key === editing.key)?.placeholders ?? []).join(' · ')}
            </span>
          </p>
          <Field label="Subject">
            <Input value={editing.subject} onChange={(subject) => setEditing({ ...editing, subject })} />
          </Field>
          <Field label="Message">
            <textarea
              className="control"
              style={{ minHeight: 280 }}
              value={editing.body}
              onChange={(event) => setEditing({ ...editing, body: event.currentTarget.value })}
            />
          </Field>

          {templates.data?.find((t) => t.key === editing.key)?.outward ? (
            <>
              <h3 className="subhead" style={{ marginTop: 20 }}>Who else gets a copy</h3>
              <p className="helper">
                Copying the team in Cc tells the supplier who is watching their order. Bcc copies
                the same people without saying so.
              </p>
              <div className="column-choices">
                <label className="column-choice">
                  <input
                    type="checkbox"
                    checked={editing.copies.ccProjectTeam}
                    onChange={() => setEditing({
                      ...editing,
                      copies: {
                        ...editing.copies,
                        ccProjectTeam: !editing.copies.ccProjectTeam,
                        // The same people cannot be both told and not told.
                        bccProjectTeam: false,
                      },
                    })}
                  />
                  <span>Cc the project team</span>
                </label>
                <label className="column-choice">
                  <input
                    type="checkbox"
                    checked={editing.copies.bccProjectTeam}
                    onChange={() => setEditing({
                      ...editing,
                      copies: {
                        ...editing.copies,
                        bccProjectTeam: !editing.copies.bccProjectTeam,
                        ccProjectTeam: false,
                      },
                    })}
                  />
                  <span>Bcc the project team</span>
                </label>
                <label className="column-choice">
                  <input
                    type="checkbox"
                    checked={editing.copies.senderInBcc}
                    onChange={() => setEditing({
                      ...editing,
                      copies: { ...editing.copies, senderInBcc: !editing.copies.senderInBcc },
                    })}
                  />
                  <span>Bcc whoever sent it — the message leaves from our address, not theirs</span>
                </label>
              </div>
            </>
          ) : null}
        </Modal>
      ) : null}
    </>
  )
}

/* ============================== outbox ==================================== */

const OUTBOX_FILTERS = [
  { value: '', label: 'Everything' },
  { value: 'QUEUED', label: 'Waiting to go' },
  { value: 'SENT', label: 'Sent' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'HELD', label: 'Held — no mail server' },
]

export const Outbox = () => {
  const api = useApi()
  const client = useQueryClient()
  const [status, setStatus] = useState('')
  const messages = useQuery({
    queryKey: ['outbox', api.companyId, status],
    queryFn: () => api.outbox(status === '' ? undefined : status),
  })
  const drain = useMutation({
    mutationFn: () => api.drainOutbox(),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['outbox', api.companyId] }) },
  })

  const columns: ReadonlyArray<ColumnDef<OutboxMessage>> = [
    { key: 'createdAt', header: 'Queued', width: 160, render: (r) => formatDateTime(r.createdAt) },
    { key: 'to', header: 'To', width: 240, render: (r) => r.toName ?? r.to },
    { key: 'subject', header: 'Subject' },
    { key: 'documentNo', header: 'Document', type: 'docno', width: 170 },
    { key: 'status', header: 'State', width: 130 },
    { key: 'attempts', header: 'Tries', align: 'right', width: 80 },
    { key: 'lastError', header: 'Last problem', render: (r) => r.lastError ?? '—' },
  ]

  if (messages.isLoading) return <Loading />
  if (messages.error) return <ErrorBlock error={messages.error} />

  return (
    <Panel
      title="Outbox"
      aside={
        <div className="row">
          <Select value={status} onChange={setStatus} options={OUTBOX_FILTERS} />
          <Button kind="ghost" disabled={drain.isPending} onClick={() => drain.mutate()}>
            Try the queue now
          </Button>
        </div>
      }
    >
      {drain.data ? (
        <Notice kind="info">
          {drain.data.sent} sent · {drain.data.failed} failed · {drain.data.held} held.
        </Notice>
      ) : null}
      <Table
        columns={columns}
        rows={messages.data ?? []}
        getKey={(row) => row.id}
        empty={<Empty title="Nothing in the outbox" body={['No email has been queued from this company.']} />}
      />
    </Panel>
  )
}
